import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { Divider, Typography } from '@mui/material';
import { mdiMagnify, mdiClose, mdiTrayArrowDown, mdiPlus, mdiTrayArrowUp } from '@mdi/js';
import Icon from '@mdi/react';
import { useNavigate } from 'react-router-dom';
import { VisibilityState } from '@tanstack/react-table';
import { Maybe } from 'graphql/jsutils/Maybe';
import {
	ErrorAlert,
	paginatorStoreSelector,
	searchStoreSelector,
	sortStoreSelector,
	usePaginatorStore,
	useSearchStore,
	useSortStore,
} from '@warehouse/shared/ui';
import { useSkipInitialEffect } from '@warehouse/shared/util';
import { IndexedTitleV2 } from '@warehouse/graphql';
import { isFeatureEnabledForCurrentTenant } from '@warehouse/shared/config';
import { defaultFilters, defaultSorters } from '@warehouse/title/feature-title-report';
import { FilterStep, FilterWithDate } from '@warehouse/shared/filters';
import OriginalLanguagesJSON from '../../assets/json-administration-profiles/availableBasicOriginalLanguage.json';

// GRAPHQL

// HOOKS
import useWindowSize, { Size } from '../../utils/hooks/useWindowSize';
import useDebounce from '../../utils/hooks/useDebounce';
import { getURLQuery } from '../../utils/hooks/useURLQuery';
import useSearchTitlesV2 from '../../utils/hooks/titles/useSearchTitlesV2';
import useTitleStatus from '../../utils/hooks/titles/useTitleStatus';
import useWorkTypes from '../../utils/hooks/titles/useWorkTypes';

// LIBRARY
import Card from '../../components/library/Card';
import TextInput from '../../components/library/TextInput';
import Button from '../../components/library/Button';
import {
	dataToFilterFields,
	FilterField,
	FilterStepWithId,
	isStep,
	prepareDataForAPI,
} from '../../utils/table/filtersUtils';
import FullTable from '../../components/table/FullTable';
import columns from './table/columns';
import { CreateTitleModal } from './createTitle';
import { ColumnDefType, convertColumnsToSortFilterModalItems } from '../../utils/table/sortingUtils';
import deepEquality from '../../utils/deepEquality';
import { useSessionShareableParams } from '../../utils/hooks/useSessionShareableParams';
import { AutoButtonGroup } from '../../components/library/AutoButtonGroup';
import { ImportMECDialog } from './importMEC/ImportMECDialog';

const Wrapper = styled.div(
	() => css`
		background-color: #dde0e3;
		box-sizing: border-box;
		display: flex;
		justify-content: center;
		width: 100%;
	`,
);

const CardWrapper = styled.div(
	({ theme }) => css`
		box-sizing: border-box;
		display: flex;
		flex-direction: column;
		gap: ${theme.spacing(2)};
		padding: ${theme.spacing(2)};
		width: 100%;
	`,
);

const TopPageWrapper = styled.div``;

const TopActionWrapper = styled.div(
	({ theme }) => css`
		align-items: center;
		display: flex;
		height: 26px;
		justify-content: space-between;
		padding: ${theme.spacing(2)};
	`,
);

const TopActionButtonWrapper = styled.div(
	({ theme }) => css`
		display: flex;
		gap: ${theme.spacing(2)};
		height: 32px;
	`,
);

const SearchBar = styled(TextInput)(
	({ theme }) => css`
		margin: 0;
		padding: ${theme.spacing(2)};
	`,
);

function removeIdFromFilter(filter?: FilterStepWithId): FilterStep | undefined {
	if (!filter) return undefined;
	return {
		step: {
			combinationOperator: filter.step.combinationOperator,
			filters:
				filter.step.filters.map((f) => {
					if (isStep(f)) return removeIdFromFilter(f)!;
					return {
						field: f.field,
						operator: f.operator,
						value: f.value,
						...(f.dateAuxiliary ? { dateAuxiliary: f.dateAuxiliary } : {}),
					} as FilterWithDate;
				}) ?? [],
		},
	};
}

function AdminTitlesView() {
	const POLL_INTERVAL = 500;
	const navigate = useNavigate();
	const size: Size = useWindowSize();
	const wrapperEl = useRef<HTMLDivElement>(null);
	const urlQuery = getURLQuery();
	const [createTitleModalIsOpen, setCreateTitleModalIsOpen] = useState<boolean>(!!urlQuery.get('titleCreation'));
	const [importMECDialogIsOpen, setImportMECDialogIsOpen] = useState<boolean>(false);
	const [searchFields, setSearchFields] = useState<string[]>([]);

	// Refs to get Table Wrapper height for table scroll
	const topPageWrapperEl = useRef<HTMLDivElement>(null);
	const [tableHeight, setTableHeight] = useState(0);

	// Search
	const { setSearchValue } = useSearchStore(searchStoreSelector.actions);
	const searchValue = useSearchStore(searchStoreSelector.searchValue);
	const debouncedSearch = useDebounce(searchValue, 500);

	// Pagination
	const { setPage, setPerPage, setTotalPages } = usePaginatorStore(paginatorStoreSelector.actions);
	const page = usePaginatorStore(paginatorStoreSelector.page);
	const perPage = usePaginatorStore(paginatorStoreSelector.perPage);

	// Sorting
	const { setSort } = useSortStore(sortStoreSelector.actions);
	const sort = useSortStore(sortStoreSelector.sort);
	const showResetSortButton = useMemo(() => !deepEquality(sort, defaultSorters), [sort, defaultSorters]);

	// Filters
	const [filter, setFilter, resetFilter] = useSessionShareableParams<FilterStepWithId | undefined>({
		uniqueKey: 'title-report',
		name: 'filter',
		defaultValue: defaultFilters,
		transformState: useCallback((f: FilterStepWithId | undefined) => removeIdFromFilter(f), []),
	});

	const isDefaultFilter = useMemo(
		() => deepEquality(defaultFilters?.step, removeIdFromFilter(filter)?.step),
		[defaultFilters, filter],
	);

	const [curatedFilters, setCuratedFilters] = useState<FilterStep | undefined>();

	// Create title options
	const createTitleOptions = useMemo(
		() => [
			{
				id: 'single',
				label: 'Create Single Title',
				icon: <Icon path={mdiPlus} size="16px" />,
				onClick: () => setCreateTitleModalIsOpen(true),
			},
			{
				id: 'multiple',
				label: 'Create Multiple Titles',
				icon: <Icon path={mdiPlus} size="16px" />,
				onClick: () => navigate('/titles/create'),
			},
			{
				id: 'importMEC',
				label: 'Import MEC',
				icon: <Icon path={mdiTrayArrowDown} size="16px" />,
				onClick: () => setImportMECDialogIsOpen(true),
			},
		],
		[],
	);

	// Title types
	const { options: workTypeOptions, workTypeDetailsOptions } = useWorkTypes();
	// Title Status
	const { options: titleStatusOptions } = useTitleStatus();

	// Dynamic table height
	useEffect(() => {
		if (topPageWrapperEl.current) {
			setTableHeight((size?.height || 1000) - topPageWrapperEl.current.clientHeight - 109);
		}
	}, [topPageWrapperEl?.current?.clientHeight, size]);

	const { error, data, networkStatus } = useSearchTitlesV2({
		debouncedSearch,
		filtering: curatedFilters,
		sorting: sort,
		pollInterval: POLL_INTERVAL,
		page,
		perPage,
		searchFields,
	});

	// Go back to first page when search, sort or filter changes
	useSkipInitialEffect(() => {
		setPage(1);
	}, [debouncedSearch, sort, filter]);

	// Filters Fields
	const [filtersFields] = useState<FilterField[]>(
		dataToFilterFields([
			{
				id: 'uuid',
				label: 'ID',
				type: 'string',
			},
			{
				id: 'licensor.label',
				label: 'Licensor',
				type: 'string',
			},
			{
				id: 'aspectRatio.aspectRatio',
				label: 'Aspect Ratio',
				type: 'float',
			},
			{
				id: 'frameRate.frameRate',
				label: 'Frame Rate',
				type: 'int',
			},
			{
				id: 'summary190',
				label: 'Summary 190',
				type: 'string',
			},
			{
				id: 'summary400',
				label: 'Summary 400',
				type: 'string',
			},
			{
				id: 'summary4000',
				label: 'Summary 4000',
				type: 'string',
			},
			{
				id: 'titleSearchDisplayNameFull',
				label: 'Title',
				type: 'string',
			},
			{
				id: 'workType.uuid',
				label: 'Title Type',
				type: 'workType',
				options: workTypeOptions,
			},
			{
				id: 'workTypeDetails.uuid',
				label: 'Title Type Detail',
				type: 'workTypeDetails',
				options: workTypeDetailsOptions,
			},
			{
				id: 'releaseYear',
				label: 'Release Year',
				type: 'date',
			},
			{
				id: 'releaseDate',
				label: 'Release Date',
				type: 'date',
			},
			{
				id: 'titleAbbreviated',
				label: 'Abbreviated Title',
				type: 'string',
			},
			{
				id: 'runLength',
				label: 'Run Length',
				type: 'string',
			},
			{
				id: 'eidrId',
				label: 'EIDR-S ID',
				type: 'string',
			},
			{
				id: 'titleDisplayUnlimited',
				label: 'Display Title',
				type: 'string',
			},
			{
				id: 'titleSort',
				label: 'Sort Title',
				type: 'string',
			},
			{
				id: 'originalTitle',
				label: 'Original Title',
				type: 'string',
			},
			{
				id: 'titleDisplay60',
				label: 'Short Title',
				type: 'string',
			},
			{
				id: 'sequenceInfo.number',
				label: 'Sequence Number',
				type: 'int',
			},
			{
				id: 'sequenceInfo.distributionNumber.distributionNumber',
				label: 'Distribution Number',
				type: 'string',
			},
			{
				id: 'sequenceInfo.houseSequence.houseSequence',
				label: 'House Sequence',
				type: 'string',
			},
			{
				id: 'titleStatus',
				label: 'Status',
				type: 'titleStatus',
				options: titleStatusOptions,
			},
			{
				id: 'createdAt',
				label: 'Created At',
				type: 'date',
			},
			{
				id: 'createdBy.name',
				label: 'Created By',
				type: 'string',
			},
			{
				id: 'editedAt',
				label: 'Last Modified',
				type: 'date',
			},
			{
				id: 'editedBy.name',
				label: 'Last Modified By',
				type: 'string',
			},
			{
				id: 'originalLanguages.uuid',
				label: 'Original Languages',
				type: 'array',
				options: OriginalLanguagesJSON.map(({ uuid, label }) => ({ value: uuid, label })),
			},
			{
				id: 'versionLanguages.uuid',
				label: 'Version Languages',
				type: 'array',
				options: OriginalLanguagesJSON.map(({ uuid, label }) => ({ value: uuid, label })),
			},
			{
				id: 'countriesOfCreation.uuid',
				label: 'Countries Of Origin',
				type: 'countries-array',
			},
			{
				id: 'countriesOfProduction.uuid',
				label: 'Production Countries',
				type: 'countries-array',
			},
		]),
	);

	// TODO(arthur): use a useMemo to compute the currated list of filter (spare one render)
	// Curate filters from Front end properties for API
	useEffect(() => {
		if (filtersFields && filter) setCuratedFilters(prepareDataForAPI({ ...filter }, filtersFields));
		if (!filter) setCuratedFilters(undefined);
	}, [filtersFields, filter]);

	const customColumns = useMemo(() => columns(), []);
	const sortModalItems = useMemo(() => convertColumnsToSortFilterModalItems(customColumns), [customColumns]);

	const refPageCount = useRef<number | undefined>(data?.searchTitlesV2?.page?.totalPage);
	if (data?.searchTitlesV2?.page?.totalPage) refPageCount.current = data?.searchTitlesV2?.page?.totalPage;

	// Sync the total pages from the back-end with the paginator store (used to make a Math.min(Math.max(1, page), totalPages) when setting the page with setPage())
	useEffect(() => {
		setTotalPages(refPageCount.current || 1);
	}, [refPageCount.current]);

	const paginationData = {
		pageSize: perPage,
		onPageSizeChange: useCallback(
			(nextPerPage: number) => {
				setPage(1);
				setPerPage(nextPerPage);
			},
			[setPerPage, setPage],
		),
		currentPage: page,
		pageCount: refPageCount.current,
		onPageChange: useCallback(
			(nextPage: number) => {
				setPage(nextPage);
			},
			[setPage],
		),
	};

	const initialColumnSticky = useMemo(() => ['licensor', 'titleSearchDisplayNameFull'], []);
	const table = useMemo(
		() =>
			customColumns.length ? (
				<FullTable<IndexedTitleV2>
					columns={customColumns}
					getSortableColumnKeyById={(id: string) => columns().find((column) => column.id === id)?.indexedTitleKey || ''}
					initialColumnSticky={initialColumnSticky}
					initialSorting={sort}
					data={data?.searchTitlesV2.documents || []}
					height={tableHeight}
					filtering={filter}
					filterFields={filtersFields}
					setFiltering={setFilter}
					resetFilter={resetFilter}
					showResetFilterButton={!isDefaultFilter}
					networkStatus={networkStatus}
					backendPagination={paginationData}
					sortModalItems={sortModalItems}
					sorting={sort}
					setSorting={setSort}
					resetSort={() => setSort(defaultSorters)}
					showResetSortButton={showResetSortButton}
					totalDocuments={data?.searchTitlesV2.page.totalDocument}
					initialVisibility={initialVisibility}
					storageUniqueKey="title-report"
					onColumnVisibilityChange={(visibility: VisibilityState) =>
						setSearchFields(computeSearchFieldsFromColumnVisibility(visibility, customColumns))
					}
				/>
			) : null,
		[
			networkStatus,
			error,
			data,
			tableHeight,
			filtersFields,
			filter,
			setFilter,
			customColumns,
			sort,
			setSort,
			initialColumnSticky,
			perPage,
			page,
		],
	);

	return (
		<Wrapper ref={wrapperEl}>
			<CardWrapper>
				<Card sx={{ minWidth: 275, overflow: 'visible' }}>
					<TopPageWrapper ref={topPageWrapperEl}>
						<TopActionWrapper>
							<Typography variant="h4Bold">Titles</Typography>
							<TopActionButtonWrapper>
								{isFeatureEnabledForCurrentTenant('title-report-export-button') && (
									<Button
										nxstyle="secondary-black"
										size="large"
										$fullHeight
										startIcon={<Icon path={mdiTrayArrowUp} size="20px" />}
									>
										Export
									</Button>
								)}
								<AutoButtonGroup
									expandButtonAriaLabel="Expand Create Title Menu"
									nxstyle="primary-blue"
									height={32}
									options={createTitleOptions}
									storeKey="title-creation"
								/>
							</TopActionButtonWrapper>
						</TopActionWrapper>
						<Divider />
						<SearchBar
							placeholder="Search titles"
							startIcon={<Icon path={mdiMagnify} size="24px" />}
							endIcon={
								<Button $disablePadding nxstyle="tertiary-light" onClick={() => setSearchValue('')}>
									<Icon path={mdiClose} size="16px" />
								</Button>
							}
							value={searchValue}
							onChange={(event) => {
								setSearchValue(event.target.value);
							}}
							height={32}
						/>
					</TopPageWrapper>
					{error && <ErrorAlert style={{ margin: 16 }} errorMessage={error.message} />}
				</Card>
				<Card sx={{ overflow: 'auto' }}>{table}</Card>
			</CardWrapper>
			<CreateTitleModal open={createTitleModalIsOpen} handleClose={() => setCreateTitleModalIsOpen(false)} />
			<ImportMECDialog open={importMECDialogIsOpen} onClose={() => setImportMECDialogIsOpen(false)} />
		</Wrapper>
	);
}

const initialVisibility = {
	summary190: false,
	summary400: false,
	summary4000: false,
	houseSequence: false,
	distributionNumber: false,
	sequenceNumber: false,
	countriesOfOrigin: false,
	productionCountries: false,
	releaseYear: false,
	frameRate: false,
	aspectRatio: false,
	titleSort: false,
	workTypeDetails: false,
	originalTitle: false,
	titleDisplay60: false,
	ratings: false,
	eidrId: false,
	titleStatus: false,
	abbreviatedTitle: false,
	titleDisplayUnlimited: false,
	createdAt: false,
	editedAt: false,
	createdBy: false,
	editedBy: false,
	versionLanguages: false,
};

function computeSearchFieldsFromColumnVisibility(
	visibility: VisibilityState,
	tableColumns: ColumnDefType<Maybe<IndexedTitleV2>>[],
): string[] {
	return Object.keys(visibility)
		.filter((key) => visibility[key])
		.flatMap((key) => {
			const column = tableColumns.find((c) => c.id === key);
			if (!column) return [];

			if (column.id === 'titleSearchDisplayNameFull' && column.indexedTitleKey) {
				return [column.id, column.indexedTitleKey];
			}
			return [column.indexedTitleKey || column.id].filter(Boolean) as string[];
		});
}

export default AdminTitlesView;
